// ─── Role-Aware Dashboard Configuration ─────────────────
// Functions that determine what each role sees on the dashboard:
// stats bar KPIs, widget ordering, and first-time defaults.

import { WIDGET_REGISTRY, type WidgetDef } from './widgetRegistry';
import { isAdminRole } from '@shared/roleAccessConfig';

// ─── Types ───────────────────────────────────────────────

export interface StatDef {
  key: string;
  label: string;
  icon: string; // lucide icon name
  /** Data path from dashboardSummary or roleSpecificStats */
  getValue: (summary: any) => string | number;
  /** Optional suffix like "%" */
  suffix?: string;
}

type HasPermissionFn = (permission: string) => boolean;

// ─── Widget Accessibility ────────────────────────────────

/**
 * Check if a widget is accessible to the current user.
 * - Empty visibleWithPermissions = accessible to all.
 * - Otherwise, user needs at least ONE of the listed permissions.
 * - Admin/superadmin can always access all widgets.
 */
export function isWidgetAccessible(
  widgetId: string,
  hasPermission: HasPermissionFn,
  userRole?: string,
): boolean {
  const def = WIDGET_REGISTRY.find(w => w.id === widgetId);
  if (!def) return false;

  // No permission restrictions = everyone can see it
  if (def.visibleWithPermissions.length === 0) return true;

  // Admins see everything
  if (isAdminRole(userRole)) return true;

  // Check OR logic: user needs at least one permission
  return def.visibleWithPermissions.some(p => hasPermission(p));
}

/**
 * Get all accessible widgets for the current user, sorted by priority.
 */
export function getAccessibleWidgets(
  hasPermission: HasPermissionFn,
  userRole?: string,
): WidgetDef[] {
  return WIDGET_REGISTRY
    .filter(w => isWidgetAccessible(w.id, hasPermission, userRole))
    .sort((a, b) => a.priority - b.priority);
}

// ─── Widget Ordering ─────────────────────────────────────

/**
 * Get the priority-ordered widget ID list for the current user.
 * Team-scoped widgets are boosted to the top for team_head/team_lead.
 */
export function getWidgetOrder(
  hasPermission: HasPermissionFn,
  userRole?: string,
  teamPositions: string[] = [],
): string[] {
  const accessible = getAccessibleWidgets(hasPermission, userRole);

  // Build a priority boost map based on role
  const boosts: Record<string, number> = {};

  // Boost actionable widgets for approvers
  if (hasPermission('leave.approve')) {
    boosts['pending_approvals'] = -100;
  }

  // Boost team widgets for team heads/leads
  if (teamPositions.includes('team_head') || teamPositions.includes('team_lead')) {
    boosts['team_availability'] = (boosts['team_availability'] ?? 0) - 50;
    boosts['team_effort_snapshot'] = (boosts['team_effort_snapshot'] ?? 0) - 50;
  }

  // Boost specialist widgets for their respective roles
  if (userRole === 'leave_manager') {
    boosts['leave_overview'] = -80;
  }
  if (userRole === 'content_manager') {
    boosts['content_pulse'] = -80;
  }
  if (userRole === 'full_analytics') {
    boosts['analytics_snapshot'] = -80;
  }

  return accessible
    .sort((a, b) => {
      const aPriority = a.priority + (boosts[a.id] ?? 0);
      const bPriority = b.priority + (boosts[b.id] ?? 0);
      return aPriority - bPriority;
    })
    .map(w => w.id);
}

// ─── First-Time Defaults ─────────────────────────────────

/**
 * Compute the list of widget IDs that should be HIDDEN for a first-time user.
 * Based on the user's system role and team positions.
 */
export function getDefaultHiddenWidgets(
  userRole: string,
  teamPositions: string[],
  hasPermission: HasPermissionFn,
): string[] {
  const hidden: string[] = [];

  for (const widget of WIDGET_REGISTRY) {
    // First check accessibility — if user can't access it, hide it
    if (!isWidgetAccessible(widget.id, hasPermission, userRole)) {
      hidden.push(widget.id);
      continue;
    }

    // Check if this widget is in the user's default set
    const isDefaultForRole = widget.defaultForRoles.includes(userRole);
    const isDefaultForPosition = teamPositions.some(pos => widget.defaultForPositions.includes(pos));

    if (!isDefaultForRole && !isDefaultForPosition) {
      hidden.push(widget.id);
    }
  }

  return hidden;
}

// ─── Stats Bar ───────────────────────────────────────────

/**
 * Get the role-aware KPI stats to display in the hero stats bar.
 * Returns up to 2 stats, picked from the pool by priority order.
 */
export function getStatsForRole(
  hasPermission: HasPermissionFn,
  userRole?: string,
  teamPositions: string[] = [],
): StatDef[] {
  const stats: StatDef[] = [];

  // Stat pool — order matters (first matching are picked, capped at 2)
  const pool: Array<StatDef & { condition: () => boolean }> = [
    // Pending approvals — top priority for approvers
    {
      key: 'pending_approvals',
      label: 'Pending Approvals',
      icon: 'CheckCircle',
      getValue: (s) => s?.roleSpecificStats?.pendingApprovalCount ?? 0,
      condition: () => hasPermission('leave.approve'),
    },
    // Org utilization — for org analytics holders / admins
    {
      key: 'org_utilization',
      label: 'Org Utilization',
      icon: 'TrendingUp',
      getValue: (s) => s?.roleSpecificStats?.orgUtilization ?? 0,
      suffix: '%',
      condition: () => hasPermission('analytics.view_org') && isAdminRole(userRole),
    },
    // Team availability — for team heads/leads
    {
      key: 'team_available_today',
      label: 'Team In Today',
      icon: 'Users',
      getValue: (s) => {
        const ta = s?.teamAvailability;
        if (!ta) return 0;
        return `${ta.inOffice + ta.wfh}/${ta.total}`;
      },
      condition: () =>
        teamPositions.includes('team_head') ||
        teamPositions.includes('team_lead'),
    },
    // Today's absences — for leave managers
    {
      key: 'today_absences',
      label: "Today's Absences",
      icon: 'UserMinus',
      getValue: (s) => s?.roleSpecificStats?.todayAbsences ?? 0,
      condition: () => userRole === 'leave_manager',
    },
    // Hours logged — universal
    {
      key: 'hours_logged',
      label: 'Hours This Week',
      icon: 'Clock',
      getValue: (s) => s?.effortSummary?.thisWeekLogged ?? 0,
      condition: () => true,
    },
    // Allocation % — universal
    {
      key: 'allocation',
      label: 'Allocated',
      icon: 'Target',
      getValue: (s) => s?.myAllocation?.allocationPercent ?? 0,
      suffix: '%',
      condition: () => true,
    },
  ];

  for (const stat of pool) {
    if (stats.length >= 2) break;
    if (stat.condition()) {
      const { condition: _condition, ...def } = stat;
      stats.push(def);
    }
  }

  return stats;
}
